import c from 'classnames';
import { LexicalEditor, LexicalNode, NodeKey } from 'lexical';
import { CreateTemplateCustomPlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/create-custom-template-placeholder-node';
import { TemplatePlaceholder } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/edit-placeholder-content-overlay';
import { PlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/shared';

type SerializedTemplateCustomPlaceholderResolverNode = {
  label: string;
  value?: string;
  type: 'template-custom-placeholder';
  version: 1;
  description?: string;
};

export class TemplateCustomPlaceholderResolver extends CreateTemplateCustomPlaceholderNode {
  __value?: string;
  __hasError: boolean;
  __isActive: boolean;

  constructor(label: string, description?: string, value?: string, key?: NodeKey) {
    super(label, description, key);

    this.__value = value;
    this.__hasError = false;
    this.__isActive = false;
  }

  static clone(
    node: TemplateCustomPlaceholderResolver,
  ): TemplateCustomPlaceholderResolver {
    return new TemplateCustomPlaceholderResolver(
      node.label,
      node.description,
      node.value,
      node.__key,
    );
  }

  get value() {
    return this.__value;
  }

  set value(newValue: string | undefined) {
    this.__value = newValue;
  }

  static getType(): string {
    return 'template-custom-placeholder';
  }

  set hasError(newValue: boolean) {
    this.__hasError = newValue;
  }

  get hasError() {
    return this.__hasError;
  }

  set isActive(newValue: boolean) {
    this.__isActive = newValue;
  }

  get isActive() {
    return this.__isActive;
  }

  getTextContent() {
    return this.isValid() ? this.value : this.label;
  }

  isValid(): this is { value: string } {
    return Boolean(this.value && this.value.trim().length > 0);
  }

  decorate(editor: LexicalEditor) {
    return (
      <TemplatePlaceholder
        value={this.value}
        description={this.description}
        onSave={(value) => {
          editor.update(() => {
            const writable = this.getWritable();

            writable.__value = value;
            writable.hasError = false;
          });
        }}
        onRemove={() => {
          editor.update(() => {
            const writable = this.getWritable();

            writable.remove();
          });
        }}
        isActive={this.isActive}
        onPopoverVisibilityChange={(isVisible) => {
          editor.update(() => {
            const writable = this.getWritable();

            writable.isActive = isVisible;
          });
        }}
      >
        <PlaceholderNode
          data-lgg-id="template-custom-placeholder-resolver-node"
          className={c({ error: this.hasError })}
        >
          {this.isValid() ? this.value : `{{${this.label}}}`}
        </PlaceholderNode>
      </TemplatePlaceholder>
    );
  }

  static importJSON(
    serializedNode: SerializedTemplateCustomPlaceholderResolverNode,
  ): LexicalNode {
    return new TemplateCustomPlaceholderResolver(
      serializedNode.label,
      serializedNode.description,
      serializedNode.value,
    );
  }

  exportJSON(): SerializedTemplateCustomPlaceholderResolverNode {
    return {
      type: 'template-custom-placeholder',
      label: this.label,
      description: this.description,
      value: this.value,
      version: 1,
    };
  }
}
